import { useCallback } from 'react';

import { JsonValue } from '@backstage/types';

import { useStorageKey } from '../behaviors';
import { useLocalStorage } from './useStorage';

const DEFAULT_MAX_ITEMS = 5;

export interface UseRecentlySelectedOptions {
  key: string;
  maxItems?: number;
}

export interface UseRecentlySelectedResult<T extends JsonValue> {
  recentItems: T[];
  addRecentItem: (item: T) => void;
  clearRecentItems: () => void;
}

export const useRecentlySelected = <T extends JsonValue>({
  key,
  maxItems = DEFAULT_MAX_ITEMS,
}: UseRecentlySelectedOptions): UseRecentlySelectedResult<T> => {
  // Create a user-specific storage key (includes org_id and email)
  const storageKey = useStorageKey(key);

  const [recentItems, setRecentItems] = useLocalStorage<T[]>(storageKey, []);

  const addRecentItem = useCallback(
    (item: T) => {
      setRecentItems(current => {
        const filtered = current.filter(
          existing => JSON.stringify(existing) !== JSON.stringify(item),
        );
        return [item, ...filtered].slice(0, maxItems);
      });
    },
    [setRecentItems, maxItems],
  );

  const clearRecentItems = useCallback(() => {
    setRecentItems([]);
  }, [setRecentItems]);

  return {
    recentItems,
    addRecentItem,
    clearRecentItems,
  };
};
